import { Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { useT } from '@/i18n/I18nProvider';
import { AiAssistant, AiTopbarButton } from '@/features/ai/components/AiAssistant';
import { IdleLogout } from '@/features/auth/IdleLogout';
import { Button, Dialog, PageSkeleton, SheetContent } from '@/shared/ui';
import { DemoSwitcher } from './DemoSwitcher';
import { Sidebar, SidebarContent } from './Sidebar';
import { GlobalSearch, HelpButton, NotificationsBell, ThemeToggle } from './TopbarActions';
import { TopbarApprovals } from './TopbarApprovals';
import { TopbarClock } from './TopbarClock';
import { UserMenu } from './UserMenu';
import { AppUpdateWatcher } from '@/features/app-update/AppUpdateWatcher';
import { ChatProvider } from '@/features/chat/hooks/ChatProvider';
import { usePermission } from '@/features/auth/permissions';

export function AppLayout() {
  const t = useT();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { pathname } = useLocation();
  const chatEnabled = usePermission('chat.use');

  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  return (
    <div className="flex min-h-dvh bg-background">
      <Sidebar />

      <Dialog open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent title={t('nav.main')}>
          <SidebarContent onNavigate={() => setDrawerOpen(false)} />
        </SheetContent>
      </Dialog>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="dark sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-sidebar-border bg-sidebar px-3 text-foreground md:px-5">
          <Button variant="ghost" size="icon" className="lg:hidden" aria-label={t('nav.openMenu')} onClick={() => setDrawerOpen(true)}>
            <Menu />
          </Button>
          <TopbarClock />
          <TopbarApprovals />
          <div className="flex min-w-0 flex-1 items-center justify-center md:min-w-[10rem]">
            <GlobalSearch />
          </div>
          <div className="flex items-center gap-1">
            <AiTopbarButton />
            <DemoSwitcher />
            <span className="hidden md:inline-flex">
              <HelpButton />
            </span>
            <ThemeToggle />
            <NotificationsBell />
            <span className="mx-2 hidden h-8 w-px bg-sidebar-border sm:block" aria-hidden />
            <UserMenu />
          </div>
          <span className="brand-gradient pointer-events-none absolute inset-x-0 -bottom-px h-0.5 opacity-80" aria-hidden />
        </header>

        <main className="min-w-0 flex-1">
          <Suspense fallback={<PageSkeleton />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      <AiAssistant />
      <IdleLogout />
      {chatEnabled && <ChatProvider />}
      <AppUpdateWatcher />
    </div>
  );
}
